import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { identify } from '@cbhq/client-analytics';

import { SessionProviderContent } from ':support/components/SessionProviderContent';
import { getStaticConfig, isLocal } from ':support/config';
import { useRedirectHandler } from ':support/hooks/useRedirectHandler';
import { logError } from ':support/utils/error-vitals/logging';
import { getDeviceId } from ':support/utils/getCoinbaseDeviceId';
import { hasRedirectUri } from ':support/utils/session-utils/redirectUriStorage';

function updateAnalyticsInstances(userUuid?: string) {
  const userId = userUuid ?? getDeviceId();

  // client analytics (also updates error vitals)
  identify({ userId });
}

// Session identity consumed by the app. cdp-core's session endpoint returns a
// flat `userId` (the Coinbase user UUID) and `email`; the former COMS profile
// fields (displayName/firstName/lastName/avatarUrl/features) were unused and are
// intentionally dropped. This is kept named `userUuid` because the value is that
// UUID and the Discord verification backend (see verify.tsx -> /VerifyUser)
// expects a `userUuid`.
export type SessionResponseSession = {
  userUuid: string;
  email: string;
};

// Requirement enum values come from cdp-core's CurrentUserOnboardingRequirement.
type SessionResponseRequirement = {
  requirement: 'email' | 'phone' | 'terms_of_service' | 'developer_platform_terms_of_service';
};

// cdp-core GET /platform/v2/users/current/session returns a flat oneOf:
//  - ready:   { userId, email }
//  - pending: { nextRequirement: { requirement } }
type SessionResponse =
  | { userId: string; email: string; nextRequirement?: null }
  | { userId?: never; email?: never; nextRequirement: SessionResponseRequirement };

type SessionContextType = {
  isLoggedIn: boolean;
  sessionResponse?: SessionResponseSession;
  navBarAvailable: boolean;
  hasRequirements: boolean;
};

export const SessionContext = createContext<SessionContextType>({
  isLoggedIn: false,
  sessionResponse: undefined,
  navBarAvailable: false,
  hasRequirements: false,
});

type SessionProviderProps = {
  children?: React.ReactNode;
  fallback?: React.ReactNode;
  locale: string;
};

async function fetchSession(baseURL: string): Promise<Response> {
  return fetch(`${baseURL}/platform/v2/users/current/session`, {
    method: 'GET',
    credentials: 'include',
  });
}

export function SessionProvider({ children, fallback }: SessionProviderProps) {
  const [errorStatus, setErrorStatus] = useState<number | undefined>();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasRequirements, setHasRequirements] = useState(false);
  const [sessionResponse, setSessionResponse] = useState<SessionResponseSession>();
  const router = useRouter();

  // Get static config for internal use
  const { disableAuth, apiBaseURL } = getStaticConfig();

  const { handleRedirect } = useRedirectHandler();

  const handleRequirementTypeError = (requirement: string) => {
    logError(`User has requirement type: ${requirement}`, {
      context: 'session-provider',
      severity: 'warning',
    });
  };

  const handleNextRequirement = useCallback(
    (requirement: SessionResponseRequirement) => {
      // Don't redirect if we're already on the terms-required page
      if (router.pathname === '/terms-required') {
        setHasRequirements(true);
        setIsLoading(false);
        return;
      }

      switch (requirement.requirement) {
        // prettier-ignore
        case 'phone':
        case 'email':
          handleRequirementTypeError(requirement.requirement);
          break;
        case 'terms_of_service':
        case 'developer_platform_terms_of_service':
          handleRequirementTypeError(requirement.requirement);
          void router.push('/terms-required').then(() => {
            setIsLoggedIn(true);
            setHasRequirements(true);
            setIsLoading(false);
          });
          break;
        default:
          handleRequirementTypeError(requirement.requirement);
      }
    },
    [setIsLoading, setIsLoggedIn, router],
  );

  const attemptLogin = useCallback(async () => {
    if (isLoggedIn) return;

    const res: Response = await fetchSession(apiBaseURL);
    if (res.ok) {
      const response = (await res.json()) as SessionResponse;
      updateAnalyticsInstances(response.userId);

      if (response.nextRequirement) {
        handleNextRequirement(response.nextRequirement);
        return;
      }

      if (response.userId) {
        // Only attempt redirect if we have a stored redirect URI
        if (hasRedirectUri()) {
          await handleRedirect();
        }
        // log them into cloud
        setIsLoggedIn(true);
        setErrorStatus(undefined);
        setIsLoading(false);
        setSessionResponse({ userUuid: response.userId, email: response.email });
        return;
      }

      // A 2xx matching neither the ready nor the pending-requirement shape is
      // malformed; surface it as an error rather than hanging on the loader.
      logError(new Error('Unexpected session response shape'), { context: 'session_provider' });
      setErrorStatus(res.status);
      setIsLoading(false);
      return;
    }

    if (res.status === 401) {
      /*
      window.location.pathname does not include query params so
      you need to combine with window.location.search
      */
      if (res.headers.get('content-type')?.includes('text/plain')) {
        const response = await res.text();
        const trimmedResponse = response.trim();

        // completely invalid login
        if (trimmedResponse === 'Unauthorized') {
          updateAnalyticsInstances();
          setIsLoggedIn(false);
          setIsLoading(false);
          return;
        }
      }

      const response = (await res.json()) as Record<string, unknown>;

      logError(
        new Error('Unexpected response from session endpoint'),
        { context: 'session_provider' },
        { response: JSON.stringify(response) },
      );

      // treat as logged out and redirect out of the app
      setIsLoggedIn(false);
      setIsLoading(false);
      return;
    }

    // any other unexpected status codes
    logError(
      new Error('Unexpected response from session endpoint'),
      { context: 'session_provider' },
      { status: res.status, statusText: res.statusText },
    );

    setErrorStatus(res.status);
    setIsLoading(false);
  }, [isLoggedIn, apiBaseURL, handleNextRequirement, handleRedirect]);

  useEffect(() => {
    try {
      // Skip session fetch when auth is disabled in local development
      if (isLocal() && disableAuth) {
        setIsLoggedIn(true);
        setIsLoading(false);
        return;
      }

      void attemptLogin();
    } catch (error) {
      throw new Error((error as Error)?.message ?? 'Error from SessionProvider');
    }
  }, [attemptLogin, disableAuth]);

  const value = useMemo((): SessionContextType => {
    return {
      isLoggedIn,
      sessionResponse,
      navBarAvailable: !hasRequirements,
      hasRequirements,
    };
  }, [isLoggedIn, sessionResponse, hasRequirements]);

  return (
    <SessionContext.Provider value={value}>
      <SessionProviderContent
        isLoading={isLoading}
        errorStatus={errorStatus}
        isLoggedIn={isLoggedIn}
        fallback={fallback}
        disableAuth={disableAuth}
      >
        {children}
      </SessionProviderContent>
    </SessionContext.Provider>
  );
}
